import { useCallback, useRef } from 'react';
import { useAnalyticsEvents, fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { fg } from '@atlassian/jira-feature-gating';
import { useIsStickyColumnsEnabled, useIsStickyColumnsVisible } from '../features/selectors.tsx';

/**
 * Fires an event when a user changes their horizontal scroll direction.
 * Used by the Frozen Column experiment to determine if frozen columns
 * reduced the amount of back and forth scrolling users do.
 */
export const useHorizontalScrollDirectionChangeAnalytics = () => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const lastScrollLeft = useRef(0);
	const lastDirection = useRef<string | null>(null);

	let isStickyColumnsEnabled = false;
	let isStickyColumnsVisible = false;
	if (fg('issue_table_frozen_columns')) {
		// eslint-disable-next-line  react-hooks/rules-of-hooks
		isStickyColumnsEnabled = useIsStickyColumnsEnabled();
		// eslint-disable-next-line  react-hooks/rules-of-hooks
		isStickyColumnsVisible = useIsStickyColumnsVisible();
	}

	return useCallback(
		(target: HTMLElement) => {
			const currentScrollLeft = target.scrollLeft;
			let direction = null;

			if (currentScrollLeft > lastScrollLeft.current) {
				direction = 'right';
			} else if (currentScrollLeft < lastScrollLeft.current) {
				direction = 'left';
			}

			// Only fire event if direction changed and it's not the first scroll
			if (direction && lastDirection.current && direction !== lastDirection.current) {
				fireUIAnalytics(createAnalyticsEvent({}), 'issueView horizontalScrollChanged', {
					actionSubjectId: 'issueView',
					direction,
					isStickyColumnsEnabled,
					isStickyColumnsVisible,
				});
			}

			if (direction) {
				lastDirection.current = direction;
			}

			lastScrollLeft.current = currentScrollLeft;
		},
		[createAnalyticsEvent, isStickyColumnsEnabled, isStickyColumnsVisible],
	);
};
